import { Link } from "react-router-dom";
import type { RiskEntry } from "@/types/content";
import RiskBadge from "./RiskBadge";

interface RiskRecordCardProps {
  entry: RiskEntry;
  variant?: "default" | "compact";
}

export default function RiskRecordCard({ entry, variant = "default" }: RiskRecordCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/blacklist/${entry.id}`}
        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/60 cursor-pointer"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700">
          <i className="ri-file-warning-line text-xl" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-700">
              {entry.name}
            </span>
            <RiskBadge level={entry.riskLevel} text={entry.riskText} />
          </span>
          <span className="mt-1 block truncate font-label text-xs text-foreground-500">
            {entry.category} · 投诉 {entry.complaintCount} 条 · 更新 {entry.updatedAt}
          </span>
        </span>
        <i className="ri-arrow-right-s-line text-xl text-foreground-400 group-hover:text-primary-600" />
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/blacklist/${entry.id}`} className="flex h-full flex-col cursor-pointer">
        <div className="relative h-44 w-full overflow-hidden bg-background-100">
          <img
            src={entry.cover}
            alt={`${entry.name} 风险记录配图`}
            title={`${entry.name} 风险记录 - 网站黑料避雷名单`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3">
            <RiskBadge level={entry.riskLevel} text={entry.riskText} />
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-center gap-2 font-label text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-50 px-2.5 py-1 text-secondary-800">{entry.category}</span>
            <span>{entry.domain}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">
            {entry.name}
          </h3>
          <p className="mt-2 line-clamp-2 font-body text-sm leading-relaxed text-foreground-600">{entry.summary}</p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {entry.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-100 px-2.5 py-1 font-label text-xs text-foreground-600"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-4 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line" />
              投诉 {entry.complaintCount} 条
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {entry.views.toLocaleString()} 次查看
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}